import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClockIcon, UserIcon, HeartIcon, ShoppingCartIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AdminService from '../services/adminService';
const CustomerDashboard = () => {
  const [activeTab, setActiveTab] = useState('menu');
  const navigate = useNavigate();
  const {
    user,
    isLoggedIn
  } = useAuth();
  // Ensure user is authenticated
  useEffect(() => {
    if (!isLoggedIn || !user) {
      navigate('/login');
    }
  }, [isLoggedIn, user, navigate]);
  // Sample menu data
  const menuCategories = [{
    id: 'main-dishes',
    name: 'Main Dishes',
    items: [{
      id: 1,
      name: 'Kare-Kare',
      description: 'A Filipino stew with a thick peanut sauce, oxtail, and vegetables.',
      price: 299.0,
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wfcm5TzpGnGaMfZVrn6ZSk/karekare.jpg',
      popular: true
    }, {
      id: 2,
      name: 'Bulalo',
      description: 'A light-colored soup made by cooking beef shanks and bone marrow with corn, cabbage, green beans and potatoes.',
      price: 320.0,
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xygfVbdEWgWP3jK3z6Uqid/Bulalo.jpg',
      popular: true
    }, {
      id: 3,
      name: 'Sinigang',
      description: 'A sour soup with pork, vegetables, and tamarind.',
      price: 280.0,
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uxM8pHhbUXC3pGWHzJs6Tf/sinigang.jpg',
      popular: false
    }]
  }, {
    id: 'sides',
    name: 'Side Dishes',
    items: [{
      id: 4,
      name: 'Lumpiang Shanghai',
      description: 'Filipino spring rolls filled with ground pork and vegetables.',
      price: 150.0,
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mcmzLGmXiKV1sfomKhiKnu/lumpi.jpg',
      popular: false
    }]
  }, {
    id: 'desserts',
    name: 'Desserts',
    items: [{
      id: 7,
      name: 'Leche Flan',
      description: 'Filipino-style crème caramel made with condensed milk and egg yolks.',
      price: 90.0,
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5xxBshb8UQQM1avDETHe8p/lecheflan.jpg',
      popular: false
    }]
  }, {
    id: 'beverages',
    name: 'Beverages',
    items: [{
      id: 8,
      name: 'Bulalo House Coolers',
      description: 'Refreshing layered drink with flavored syrups, perfect to pair with Filipino dishes.',
      price: 85.0,
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hjDux2hW8aSsDw793FRQPM/489862575_122140821638423180_6935283752305353880_n.jpg',
      popular: true
    }, {
      id: 9,
      name: 'Calamansi Juice',
      description: 'Fresh Filipino citrus juice, sweet and tangy.',
      price: 65.0,
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uoT5jSdYi5K9vA5bdPWizb/calamansi.jpg',
      popular: false
    }]
  }];
  // Order history state
  const [orderHistory, setOrderHistory] = useState([]);
  // Favorites state
  const [favorites, setFavorites] = useState([]);
  // Profile state
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    addresses: []
  });
  // Load user data on component mount
  useEffect(() => {
    // Check if user is logged in
    if (!isLoggedIn || !user) {
      return;
    }
    const loadOrdersFromDatabase = async () => {
      try {
        // Load orders from database instead of localStorage
        const allOrders = await AdminService.getOrders();
        // Filter orders for the current user
        const userOrders = allOrders.filter(order => order.User_ID === user.User_ID);
        // Get detailed order information
        const detailedOrders = await Promise.all(userOrders.map(async order => {
          const orderDetails = await AdminService.getOrderDetails(order.OrderID);
          return {
            id: order.OrderID,
            date: order.Order_Date,
            status: order.Order_Status,
            total: order.Total_Amount,
            items: orderDetails.items.map((item: any) => ({
              name: item.name,
              quantity: item.Quantity,
              price: item.price
            })),
            customerEmail: user.Email,
            customerName: user.Name
          };
        }));
        setOrderHistory(detailedOrders);
      } catch (error) {
        console.error('Error loading orders from database:', error);
      }
    };
    loadOrdersFromDatabase();
    // Set up interval to refresh orders every 5 seconds
    const intervalId = setInterval(() => {
      loadOrdersFromDatabase();
    }, 5000);
    // Load favorites from localStorage
    const savedFavorites = localStorage.getItem(`favorites_${user.email}`);
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
    // Load profile from localStorage
    const savedProfile = localStorage.getItem(`profile_${user.email}`);
    if (savedProfile) {
      setProfile(JSON.parse(savedProfile));
    } else if (user) {
      // Initialize with data from auth context if available
      setProfile({
        firstName: user.name ? user.name.split(' ')[0] : '',
        lastName: user.name ? user.name.split(' ')[1] || '' : '',
        email: user.email || '',
        contactNumber: '',
        addresses: []
      });
    }
    return () => clearInterval(intervalId);
  }, [user, isLoggedIn]);
  // Sample cart data
  const [cart, setCart] = useState([]);
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    // Load cart from localStorage
    const savedCart = localStorage.getItem(`cart_${user.email}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, [user, isLoggedIn]);
  // Update localStorage whenever cart changes
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    localStorage.setItem(`cart_${user.email}`, JSON.stringify(cart));
    // Trigger a custom event to notify other components about cart changes
    const event = new Event('cartUpdated');
    window.dispatchEvent(event);
  }, [cart, user, isLoggedIn]);
  const addToCart = item => {
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    if (existingItem) {
      setCart(cart.map(cartItem => cartItem.id === item.id ? {
        ...cartItem,
        quantity: cartItem.quantity + 1
      } : cartItem));
    } else {
      setCart([...cart, {
        ...item,
        quantity: 1
      }]);
    }
  };
  const removeFromCart = itemId => {
    setCart(cart.filter(item => item.id !== itemId));
  };
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
      return;
    }
    setCart(cart.map(item => item.id === itemId ? {
      ...item,
      quantity: newQuantity
    } : item));
  };
  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };
  // Toggle favorite status
  const toggleFavorite = item => {
    if (!isLoggedIn || !user) {
      navigate('/login');
      return;
    }
    const existingIndex = favorites.findIndex(fav => fav.id === item.id);
    let newFavorites = [];
    if (existingIndex >= 0) {
      // Remove from favorites
      newFavorites = favorites.filter(fav => fav.id !== item.id);
    } else {
      // Add to favorites
      newFavorites = [...favorites, item];
    }
    setFavorites(newFavorites);
    // Save to localStorage
    localStorage.setItem(`favorites_${user.email}`, JSON.stringify(newFavorites));
  };
  // Check if an item is in favorites
  const isFavorite = itemId => {
    return favorites.some(item => item.id === itemId);
  };
  // Handle profile update
  const handleProfileUpdate = e => {
    e.preventDefault();
    if (!isLoggedIn || !user) {
      navigate('/login');
      return;
    }
    // Save profile to localStorage
    localStorage.setItem(`profile_${user.email}`, JSON.stringify(profile));
    alert('Profile updated successfully!');
  };
  // Add new address
  const addNewAddress = () => {
    setProfile({
      ...profile,
      addresses: [...profile.addresses, {
        id: Date.now(),
        name: 'New Address',
        address: ''
      }]
    });
  };
  // Update address
  const updateAddress = (id, field, value) => {
    setProfile({
      ...profile,
      addresses: profile.addresses.map(addr => addr.id === id ? {
        ...addr,
        [field]: value
      } : addr)
    });
  };
  // Remove address
  const removeAddress = id => {
    setProfile({
      ...profile,
      addresses: profile.addresses.filter(addr => addr.id !== id)
    });
  };
  // Sample recommendations based on order history and favorites
  const recommendations = favorites.length > 0 ? favorites.slice(0, 2) : [{
    id: 1,
    name: 'Kare-Kare',
    description: 'Popular choice for first-time customers',
    price: 299.0,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wfcm5TzpGnGaMfZVrn6ZSk/karekare.jpg'
  }, {
    id: 8,
    name: 'Bulalo House Coolers',
    description: 'Perfect pairing with our main dishes',
    price: 85.0,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hjDux2hW8aSsDw793FRQPM/489862575_122140821638423180_6935283752305353880_n.jpg'
  }];
  const proceedToCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty. Add some items before checking out.');
      return;
    }
    // Save cart to localStorage
    localStorage.setItem(`cart_${user.email}`, JSON.stringify(cart));
    navigate('/checkout');
  };
  // Get user's first name for greeting
  const userFirstName = profile.firstName || (user && user.name ? user.name.split(' ')[0] : 'Guest');
  // If still loading or not authenticated, show loading state
  if (!isLoggedIn || !user) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-875">
        <div className="text-center" data-id="element-876">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800 mx-auto" data-id="element-877"></div>
          <p className="mt-4 text-amber-800" data-id="element-878">Loading dashboard...</p>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen" data-id="element-879">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-880">
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-881">
          {/* Dashboard Header */}
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-882">
            <h1 className="text-2xl font-semibold text-gray-800" data-id="element-883">
              Welcome, {userFirstName}!
            </h1>
            <p className="text-sm text-gray-600" data-id="element-884">
              {orderHistory.length > 0 ? `You have ${orderHistory.length} order(s) and 150 loyalty points` : 'Place your first order to earn loyalty points'}
            </p>
          </div>
          {/* Dashboard Tabs */}
          <div className="border-b border-gray-200" data-id="element-885">
            <nav className="flex -mb-px" data-id="element-886">
              <button onClick={() => setActiveTab('menu')} className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'menu' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-887">
                <ShoppingCartIcon className="w-5 h-5 mx-auto mb-1" data-id="element-888" />
                Menu
              </button>
              <button onClick={() => setActiveTab('orders')} className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'orders' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-889">
                <ClockIcon className="w-5 h-5 mx-auto mb-1" data-id="element-890" />
                Orders
              </button>
              <button onClick={() => setActiveTab('profile')} className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'profile' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-891">
                <UserIcon className="w-5 h-5 mx-auto mb-1" data-id="element-892" />
                Profile
              </button>
              <button onClick={() => setActiveTab('favorites')} className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'favorites' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-893">
                <HeartIcon className="w-5 h-5 mx-auto mb-1" data-id="element-894" />
                Favorites
              </button>
            </nav>
          </div>
          {/* Dashboard Content */}
          <div className="p-6" data-id="element-895">
            {activeTab === 'menu' && <div className="flex flex-col lg:flex-row gap-8" data-id="element-896">
                {/* Menu Items */}
                <div className="lg:w-2/3" data-id="element-897">
                  {/* AI Recommendations */}
                  <div className="mb-8" data-id="element-898">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-899">
                      Recommended for You
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-900">
                      {recommendations.map(item => <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex" data-id="element-901">
                          <img src={item.image} alt={item.name} className="w-24 h-24 object-cover" data-id="element-902" />
                          <div className="p-4 flex flex-col justify-between flex-grow" data-id="element-903">
                            <div data-id="element-904">
                              <h3 className="font-medium text-gray-900" data-id="element-905">
                                {item.name}
                              </h3>
                              <p className="text-sm text-gray-500" data-id="element-906">
                                {item.description}
                              </p>
                            </div>
                            <div className="flex justify-between items-center mt-2" data-id="element-907">
                              <span className="font-semibold" data-id="element-908">
                                ₱{item.price.toFixed(2)}
                              </span>
                              <div className="flex space-x-2" data-id="element-909">
                                <button onClick={() => toggleFavorite(item)} className={`p-1 rounded-full ${isFavorite(item.id) ? 'text-red-500 hover:text-red-600' : 'text-gray-400 hover:text-gray-500'}`} data-id="element-910">
                                  <HeartIcon className="w-5 h-5" data-id="element-911" />
                                </button>
                                <button onClick={() => addToCart(item)} className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700" data-id="element-912">
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  {/* Menu Categories */}
                  {menuCategories.map(category => <div key={category.id} className="mb-8" data-id="element-913">
                      <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-914">
                        {category.name}
                      </h2>
                      <div className="grid grid-cols-1 gap-4" data-id="element-915">
                        {category.items.map(item => <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex" data-id="element-916">
                            <img src={item.image} alt={item.name} className="w-24 h-24 object-cover" data-id="element-917" />
                            <div className="p-4 flex flex-col justify-between flex-grow" data-id="element-918">
                              <div data-id="element-919">
                                <div className="flex justify-between" data-id="element-920">
                                  <h3 className="font-medium text-gray-900" data-id="element-921">
                                    {item.name}
                                  </h3>
                                  {item.popular && <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full" data-id="element-922">
                                      Popular
                                    </span>}
                                </div>
                                <p className="text-sm text-gray-500 mt-1" data-id="element-923">
                                  {item.description}
                                </p>
                              </div>
                              <div className="flex justify-between items-center mt-2" data-id="element-924">
                                <span className="font-semibold" data-id="element-925">
                                  ₱{item.price.toFixed(2)}
                                </span>
                                <div className="flex space-x-2" data-id="element-926">
                                  <button onClick={() => toggleFavorite(item)} className={`p-1 rounded-full ${isFavorite(item.id) ? 'text-red-500 hover:text-red-600' : 'text-gray-400 hover:text-gray-500'}`} data-id="element-927">
                                    <HeartIcon className="w-5 h-5" data-id="element-928" />
                                  </button>
                                  <button onClick={() => addToCart(item)} className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700" data-id="element-929">
                                    Add to Cart
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>)}
                </div>
                {/* Cart */}
                <div className="lg:w-1/3" data-id="element-930">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-4" data-id="element-931">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-932">
                      Your Order
                    </h2>
                    {cart.length === 0 ? <div className="text-gray-500 text-center py-6" data-id="element-933">
                        <ShoppingCartIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" data-id="element-934" />
                        <p data-id="element-935">Your cart is empty</p>
                        <p className="text-sm mt-2" data-id="element-936">
                          Add items from the menu to get started
                        </p>
                      </div> : <>
                        <div className="space-y-3 mb-4" data-id="element-937">
                          {cart.map(item => <div key={item.id} className="flex justify-between items-center" data-id="element-938">
                              <div className="flex-grow" data-id="element-939">
                                <h4 className="font-medium text-gray-900" data-id="element-940">
                                  {item.name}
                                </h4>
                                <div className="flex items-center mt-1" data-id="element-941">
                                  <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-942">
                                    -
                                  </button>
                                  <span className="mx-2" data-id="element-943">{item.quantity}</span>
                                  <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-944">
                                    +
                                  </button>
                                </div>
                              </div>
                              <div className="text-right" data-id="element-945">
                                <p className="font-medium" data-id="element-946">
                                  ₱{(item.price * item.quantity).toFixed(2)}
                                </p>
                                <button onClick={() => removeFromCart(item.id)} className="text-red-600 text-xs hover:text-red-800" data-id="element-947">
                                  Remove
                                </button>
                              </div>
                            </div>)}
                        </div>
                        <div className="border-t border-gray-200 pt-4 space-y-2" data-id="element-948">
                          <div className="flex justify-between text-sm" data-id="element-949">
                            <span className="text-gray-600" data-id="element-950">Subtotal</span>
                            <span className="font-medium" data-id="element-951">
                              ₱{calculateTotal().toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-952">
                            <span className="text-gray-600" data-id="element-953">Delivery Fee</span>
                            <span className="font-medium" data-id="element-954">₱20.00</span>
                          </div>
                          <div className="flex justify-between font-semibold" data-id="element-955">
                            <span data-id="element-956">Total</span>
                            <span data-id="element-957">₱{(calculateTotal() + 20).toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="mt-6" data-id="element-958">
                          <button onClick={proceedToCheckout} className="w-full py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-959">
                            Proceed to Checkout
                          </button>
                        </div>
                      </>}
                  </div>
                </div>
              </div>}
            {activeTab === 'orders' && <div data-id="element-960">
                <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-961">
                  Your Order History
                </h2>
                {orderHistory.length === 0 ? <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-962">
                    <ClockIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" data-id="element-963" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-964">
                      No orders yet
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-965">
                      You haven't placed any orders yet.
                    </p>
                    <button onClick={() => setActiveTab('menu')} className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-966">
                      Browse Menu
                    </button>
                  </div> : <div className="space-y-6" data-id="element-967">
                    {orderHistory.map(order => <div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-968">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center" data-id="element-969">
                          <div data-id="element-970">
                            <span className="font-semibold text-gray-900" data-id="element-971">
                              {order.id}
                            </span>
                            <span className="text-sm text-gray-500 ml-2" data-id="element-972">
                              {new Date(order.date).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-973">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' : order.status === 'Preparing' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-974">
                              {order.status}
                            </span>
                            <Link to={`/order/${order.id}`} className="ml-4 text-sm text-orange-600 hover:text-orange-800" data-id="element-975">
                              View Details
                            </Link>
                          </div>
                        </div>
                        <div className="p-4" data-id="element-976">
                          <div className="space-y-2" data-id="element-977">
                            {order.items.map((item, index) => <div key={index} className="flex justify-between" data-id="element-978">
                                <div className="flex" data-id="element-979">
                                  <span className="text-gray-600" data-id="element-980">
                                    {item.quantity} x
                                  </span>
                                  <span className="ml-2" data-id="element-981">{item.name}</span>
                                </div>
                                <span className={`${item.price < 0 ? 'text-green-600' : 'text-gray-900'}`} data-id="element-982">
                                  ₱{(item.price * item.quantity).toFixed(2)}
                                </span>
                              </div>)}
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between" data-id="element-983">
                            <span className="font-semibold" data-id="element-984">Total</span>
                            <span className="font-semibold" data-id="element-985">
                              ₱{order.total.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>}
            {activeTab === 'profile' && <div data-id="element-986">
                <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-987">
                  Your Profile
                </h2>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-988">
                  <form onSubmit={handleProfileUpdate} data-id="element-989">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-990">
                      <div data-id="element-991">
                        <h3 className="text-base font-medium text-gray-900 mb-4" data-id="element-992">
                          Personal Information
                        </h3>
                        <div className="space-y-4" data-id="element-993">
                          <div data-id="element-994">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-995">
                              First Name
                            </label>
                            <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" value={profile.firstName} onChange={e => setProfile({
                          ...profile,
                          firstName: e.target.value
                        })} data-id="element-996" />
                          </div>
                          <div data-id="element-997">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-998">
                              Last Name
                            </label>
                            <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" value={profile.lastName} onChange={e => setProfile({
                          ...profile,
                          lastName: e.target.value
                        })} data-id="element-999" />
                          </div>
                          <div data-id="element-1000">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1001">
                              Email
                            </label>
                            <input type="email" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" value={profile.email} onChange={e => setProfile({
                          ...profile,
                          email: e.target.value
                        })} data-id="element-1002" />
                          </div>
                          <div data-id="element-1003">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1004">
                              Contact Number
                            </label>
                            <input type="tel" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" value={profile.contactNumber} onChange={e => setProfile({
                          ...profile,
                          contactNumber: e.target.value
                        })} placeholder="+63 912 345 6789" data-id="element-1005" />
                          </div>
                        </div>
                      </div>
                      <div data-id="element-1006">
                        <h3 className="text-base font-medium text-gray-900 mb-4" data-id="element-1007">
                          Delivery Addresses
                        </h3>
                        <div className="space-y-4" data-id="element-1008">
                          {profile.addresses.length === 0 ? <div className="text-gray-500 text-center py-4" data-id="element-1009">
                              <p data-id="element-1010">No addresses saved yet</p>
                            </div> : profile.addresses.map(address => <div key={address.id} className="border border-gray-300 rounded-md p-3" data-id="element-1011">
                                <div className="flex justify-between items-start" data-id="element-1012">
                                  <div className="w-full" data-id="element-1013">
                                    <input type="text" className="font-medium w-full border-0 p-0 focus:ring-0 bg-transparent" value={address.name} onChange={e => updateAddress(address.id, 'name', e.target.value)} placeholder="Address Name (e.g. Home, Office)" data-id="element-1014" />
                                    <textarea className="text-sm text-gray-600 mt-1 w-full border-0 p-0 focus:ring-0 bg-transparent" value={address.address} onChange={e => updateAddress(address.id, 'address', e.target.value)} placeholder="Full address" rows={3} data-id="element-1015" />
                                  </div>
                                  <div className="flex space-x-2 ml-2" data-id="element-1016">
                                    <button type="button" onClick={() => removeAddress(address.id)} className="text-red-600 hover:text-red-800" data-id="element-1017">
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>)}
                          <button type="button" onClick={addNewAddress} className="text-orange-600 hover:text-orange-800 flex items-center" data-id="element-1018">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1019">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" data-id="element-1020"></path>
                            </svg>
                            Add New Address
                          </button>
                        </div>
                        <h3 className="text-base font-medium text-gray-900 mb-4 mt-6" data-id="element-1021">
                          Change Password
                        </h3>
                        <div className="space-y-4" data-id="element-1022">
                          <div data-id="element-1023">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1024">
                              Current Password
                            </label>
                            <input type="password" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" data-id="element-1025" />
                          </div>
                          <div data-id="element-1026">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1027">
                              New Password
                            </label>
                            <input type="password" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" data-id="element-1028" />
                          </div>
                          <div data-id="element-1029">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1030">
                              Confirm New Password
                            </label>
                            <input type="password" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" data-id="element-1031" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end" data-id="element-1032">
                      <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-1033">
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              </div>}
            {activeTab === 'favorites' && <div data-id="element-1034">
                <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1035">
                  Your Favorites
                </h2>
                {favorites.length === 0 ? <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-1036">
                    <HeartIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" data-id="element-1037" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1038">
                      No favorites yet
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-1039">
                      Save your favorite dishes for quick access.
                    </p>
                    <button onClick={() => setActiveTab('menu')} className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-1040">
                      Browse Menu
                    </button>
                  </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1041">
                    {favorites.map(item => <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-1042">
                        <img src={item.image} alt={item.name} className="w-full h-48 object-cover" data-id="element-1043" />
                        <div className="p-4" data-id="element-1044">
                          <h3 className="font-medium text-gray-900" data-id="element-1045">
                            {item.name}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-1046">
                            {item.description}
                          </p>
                          <div className="flex justify-between items-center mt-4" data-id="element-1047">
                            <span className="font-semibold" data-id="element-1048">
                              ₱{item.price.toFixed(2)}
                            </span>
                            <div className="flex space-x-2" data-id="element-1049">
                              <button onClick={() => toggleFavorite(item)} className="text-red-500 hover:text-red-600" data-id="element-1050">
                                <HeartIcon className="w-5 h-5" data-id="element-1051" />
                              </button>
                              <button onClick={() => addToCart(item)} className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700" data-id="element-1052">
                                Add to Cart
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default CustomerDashboard;